import { useEffect, useRef, useState } from 'react';
import { isBackupStopLatched, subscribeBackupStopLatch } from '../utils/backupStopLatch';

const IDLE = {
    running: false, kind: null, stage: null, done: 0, total: 0,
    currentFile: null, failed: 0, tool: null, bytesReceived: 0, bytesTotal: 0,
    pass: null,
};

// Restore phases other than the long files leg ('files-restore') — snapshot
// apply, cross-tool and DJ-data merge legs. Each is seconds-to-minutes; they
// show as a pulse, like snapshots (no meaningful ratio to draw).
const RESTORE_PHASES = new Set(['downloading', 'decompressing', 'applying', 'relinking', 'merging', 'writing']);

// Files-engine stages that end its run. 'snoozed' is broadcast by the snooze
// handler rather than the engine and means the same thing.
const FILES_TERMINAL = new Set(['done', 'stopped', 'snoozed']);

// How long to wait on a silent stream before CHECKING whether the run is over
// (see armStale — silence alone never settles the toast). Without any stale
// handling the toast kept claiming "Backing up… 104 of 3,564" indefinitely
// when the stream died without a terminal event — an engine crash, a
// main-process restart, a run that ended down a path that never emits.
const PROGRESS_STALE_MS = 6000;

// A backup that ends keeps the toast up this long before it settles: long
// enough to bridge a hand-off the user experiences as one backup (Retry
// all's snapshot pass and the files pass it kicks; the engine's queued
// follow-up run), and to give the finished state a beat on screen.
const END_HOLD_MS = 800;
// Once up, the toast stays at least this long however fast the backup ends.
// A Resume with nothing left to do settles in a few milliseconds, and a card
// that flashes for two frames reads as a glitch, not as feedback.
const MIN_SHOW_MS = 1500;

// Dev QA hook: every mounted instance registers here so a driver script can
// simulate engine progress (window.__mockBackupProgress) without uploading
// anything real. Never populated in production builds.
const _devHandlers = new Set();

/**
 * Live state of Cloud Backup for UI that reacts while it works (the Library's
 * cloud column + header progress line, the app-wide backup toast). Fed by the
 * engine's `backup:progress` events.
 *
 * Two layers, because one click is a SEQUENCE: the pass envelope
 * (`{ phase:'pass', event:'start'|'end' }`, from the main process — see
 * backup/backupPass.js) says a backup is under way from the click until its
 * last step is over, and the step events inside it say what it is doing right
 * now — a DJ-data snapshot per app (`building` → `uploading` → `done`), the
 * files run (`phase:'files'` with stage, counts and currentFile; a terminal
 * stage ends it), or a restore. The toast used to follow the steps alone and
 * so appeared and vanished at every hand-off; now `running` is true for the
 * whole pass, and the step only changes what the toast says.
 *
 * What shows: a pass the USER started (enable, Check now, Resume, Retry all)
 * from the moment it opens; a background pass (the DJ-database watcher, the
 * retry ladder, startup) only once bytes move — most background passes are
 * change checks that find nothing — and then for the rest of that pass. A
 * bare step with no envelope shows on the same terms.
 *
 * Desktop only — inert (always idle) when the backup bridge is absent.
 */
export default function useBackupRun() {
    const backupApi = typeof window !== 'undefined' ? window.electron?.backup : null;
    const [run, setRun] = useState(IDLE);
    const staleTimer = useRef(null);
    const holdTimer = useRef(null);

    useEffect(() => {
        if (!backupApi?.onProgress) return undefined;
        let cancelled = false;
        // The live model. Plain object, not state: several events can land
        // in one tick and every one of them recomputes what is on screen.
        //   pass    — the open envelope, if any: { id, userInitiated, sawStep }
        //   step    — the step reporting right now, if any: { kind, stage, fields }
        //   shown   — the toast is up for this pass: a user's pass has it from
        //             the click, a background one earns it by moving bytes —
        //             and keeps it through the pass's quiet moments
        //   shownAt — when it went up (for MIN_SHOW_MS)
        const m = { pass: null, step: null, shown: false, shownAt: 0 };

        const clearHold = () => {
            if (holdTimer.current) { clearTimeout(holdTimer.current); holdTimer.current = null; }
        };
        const clearStale = () => {
            if (staleTimer.current) { clearTimeout(staleTimer.current); staleTimer.current = null; }
        };

        // What belongs on screen, from the model alone.
        const visible = () => {
            const { pass, step } = m;
            // NOT gated on the backup stop latch — that latch belongs to the
            // upload engine, and a restore the user just started must show
            // regardless.
            if (step?.kind === 'restore') return true;
            // Stop is not instant: the engine keeps emitting for the files
            // already in flight. Rendering the wind-down made the toast
            // disappear, come back and disappear again for one click. The
            // user's decision wins from the moment they make it, and only an
            // explicit Resume / Back up now / settings change lifts it.
            if (isBackupStopLatched()) return false;
            if (pass && (pass.userInitiated || m.shown)) return true;
            // A files event only ever follows a change check that found work
            // to do, so every one of them is a real backup — including the
            // count-only event that opens a run and the quiet ones between
            // negotiated batches. Those used to read as the run going idle,
            // and the toast blinked at every batch boundary.
            if (step?.kind === 'files') return true;
            // A snapshot is a run from its first upload byte. The 'building'
            // check before it — parse, hash, compare with the stored copy —
            // usually finds nothing (the DJ-database watcher runs it on every
            // fs event), and an app-wide "Backing up Traktor" for a check
            // that backs up nothing taught the user the toast lies. Inside a
            // pass that is already on screen it shows as the step it is.
            if (step?.kind === 'snapshot' && step.stage === 'uploading') return true;
            return false;
        };

        const settled = () => {
            clearStale();
            m.shown = false;
            m.shownAt = 0;
            setRun(IDLE);
        };

        // Nothing belongs on screen any more. Hold before settling, and look
        // again at the end of the hold — a new pass or step may have arrived.
        // Stop skips the hold: that click must land at once.
        const settle = () => {
            if (holdTimer.current) return; // already counting down
            if (!m.shownAt || isBackupStopLatched()) { settled(); return; }
            const now = Date.now();
            const until = Math.max(now + END_HOLD_MS, m.shownAt + MIN_SHOW_MS);
            holdTimer.current = setTimeout(() => {
                holdTimer.current = null;
                if (cancelled) return;
                if (visible()) { publish(); return; }
                settled();
            }, until - now);
        };

        const publish = () => {
            if (cancelled) return;
            if (!visible()) { settle(); return; }
            clearHold();
            if (!m.shownAt) m.shownAt = Date.now();
            if (m.pass) m.shown = true;
            const { pass, step } = m;
            setRun({
                ...IDLE,
                ...(step?.fields || {}),
                running: true,
                kind: step?.kind || 'pass',
                // With no step reporting, 'starting' is the moment before
                // the first one and 'between' a hand-off (the files engine
                // enumerating after the last snapshot, say).
                stage: step ? (step.stage || null) : (pass?.sawStep ? 'between' : 'starting'),
                pass: pass ? { userInitiated: pass.userInitiated } : null,
            });
            armStale();
        };

        // Silence is NOT proof the run ended. The engine goes quiet for a
        // whole negotiate round-trip (a batch of files over the network) and
        // for the seconds a DJ database takes to parse, so ask before
        // believing it: only settle if it agrees nothing is running.
        const armStale = () => {
            clearStale();
            staleTimer.current = setTimeout(async () => {
                if (cancelled) return;
                let s = null;
                try { s = await backupApi.getStatus?.(); } catch { s = null; }
                if (cancelled) return;
                if (s && !s.error && (s.pass || s.files?.state === 'running' || s.snapshotRunning || s.restore?.active)) {
                    armStale(); // still working, just quiet — keep waiting
                    return;
                }
                // The engine says nothing is running, so whatever we hold is
                // a stream that died without its terminal event (an engine
                // crash, a main-process restart).
                m.pass = null;
                m.step = null;
                publish();
            }, PROGRESS_STALE_MS);
        };

        const handle = (p) => {
            if (cancelled || !p) return;
            if (p.phase === 'pass') {
                if (p.event === 'start') {
                    // Re-announced with the same id = the open pass changing
                    // hands (a click landing during a background check), not
                    // a second pass.
                    const same = !!m.pass && m.pass.id === p.id;
                    m.pass = { id: p.id, userInitiated: !!p.userInitiated, sawStep: same ? m.pass.sawStep : false };
                    if (!same) m.shown = false;
                } else if (p.event === 'end' && (!m.pass || p.id == null || m.pass.id === p.id)) {
                    m.pass = null;
                    m.shown = false;
                }
                publish();
                return;
            }
            // Restores ride the same channel. The long leg carries counts and
            // bytes; the merge/apply legs pulse.
            if (p.phase === 'files-restore') {
                // The engine's explicit end-of-run marker.
                if (p.final) { m.step = null; publish(); return; }
                m.step = {
                    kind: 'restore',
                    // 'checking' = the pre-download hash-verify pass, rendered
                    // as a pulse (its counts are verified files, not downloads).
                    stage: p.stage || null,
                    fields: {
                        done: p.done || 0,
                        total: p.total || 0,
                        currentFile: p.currentFile || null,
                        bytesReceived: p.bytesReceived || 0,
                        bytesTotal: p.bytesTotal || 0,
                    },
                };
                publish();
                return;
            }
            if (RESTORE_PHASES.has(p.phase)) {
                m.step = { kind: 'restore', stage: p.phase, fields: { tool: p.tool || p.target || null } };
                publish();
                return;
            }
            if (p.phase === 'files') {
                if (m.pass) m.pass.sawStep = true;
                if (FILES_TERMINAL.has(p.stage)) { m.step = null; publish(); return; }
                m.step = {
                    kind: 'files',
                    stage: p.stage || null,
                    fields: {
                        done: p.libraryDone || 0,
                        total: p.libraryTotal || 0,
                        currentFile: p.currentFile || null,
                        // Without this the count looks frozen whenever uploads
                        // are failing — same "104 of 3,564" forever, no reason.
                        failed: (p.libraryFailed || 0) + (p.librarySkippedQuota || 0),
                    },
                };
                publish();
                return;
            }
            // DJ-data snapshots carry no counts — one archive per app, not a
            // queue — so the toast names the app and pulses instead of a bar.
            if (p.phase === 'building' || p.phase === 'uploading') {
                if (m.pass) m.pass.sawStep = true;
                m.step = { kind: 'snapshot', stage: p.phase, fields: { tool: p.tool || null } };
                publish();
                return;
            }
            if (p.phase === 'done') {
                // Ends one app's snapshot, or a restore. The pass, if there
                // is one, carries the toast across to the next step.
                if (m.step && (m.step.kind === 'snapshot' || m.step.kind === 'restore')) m.step = null;
                publish();
            }
        };

        const off = backupApi.onProgress(handle);
        // Stop / Resume flip what belongs on screen without an engine event.
        const offLatch = subscribeBackupStopLatch(() => publish());
        // Seed from status: a backup started before mount emits no event
        // until its next throttle tick, which can be seconds away.
        backupApi.getStatus?.().then((s) => {
            if (cancelled || !s || s.error) return;
            if (s.pass && !m.pass) {
                m.pass = { id: s.pass.id, userInitiated: !!s.pass.userInitiated, sawStep: false };
            }
            if (!m.step) {
                if (s.restore?.active) {
                    const prog = s.restore.progress;
                    m.step = {
                        kind: 'restore',
                        stage: null,
                        fields: {
                            done: prog?.done || 0,
                            total: prog?.total || 0,
                            currentFile: prog?.currentFile || null,
                            bytesReceived: prog?.bytesReceived || 0,
                            bytesTotal: prog?.bytesTotal || 0,
                        },
                    };
                } else if (s.files?.state === 'running') {
                    m.step = { kind: 'files', stage: null, fields: {} };
                }
            }
            publish();
        }).catch(() => {});
        if (process.env.NODE_ENV !== 'production') {
            _devHandlers.add(handle);
            window.__mockBackupProgress = (p) => _devHandlers.forEach((h) => h(p));
        }
        return () => {
            cancelled = true;
            clearStale();
            clearHold();
            _devHandlers.delete(handle);
            offLatch();
            if (off) off();
        };
    }, [backupApi]);

    return run;
}
